/**
 * Parametros do P-PAR-4 (modulo `parameters` do backend): as regras e os dados de
 * acesso da tela "Consultas PJ e Pre-validacao".
 *
 * As classes de parcelamento, os limites por cluster, o cosseguro e os
 * resseguradores tem API e nao tem tela (decisao 9 da secao 11.6 do plano 49): o
 * OnPoint nao tem tela para eles no inventario, e por isso nao ha chamada aqui.
 *
 * O P-PAR-3 acrescentou, no fim, o endosso de aniversario, a renovacao e o catalogo de
 * indices economicos, do mesmo modulo do backend.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { api } from '../client';

/** Os dois botoes da tela do OnPoint, nesta ordem. */
export type ConsultationKind = 'pre_validacao' | 'pj';

/** Governanca da versao (rascunho, ativa, revogada). */
export interface ParameterGovernance {
  version: number;
  status: 'rascunho' | 'ativa' | 'revogada';
  based_on_version: number | null;
  notes: string;
  /** Quem le criou ou editou a versao, e por isso nao a ativa. */
  authored_by_you: boolean;
  /**
   * Quem pede e autor e foi liberado nominalmente da segregacao (ADR 0091): pode
   * ativar mesmo assim, como autoaprovacao. So vem verdadeiro junto com a autoria.
   */
  may_self_approve?: boolean;
  created_label: string;
  updated_label: string;
  activated_label: string;
  activated_at: string | null;
  justification: string;
  effective_from: string | null;
  revoked_label: string;
  revoked_at: string | null;
  revocation_reason: string;
  created_at: string;
  updated_at: string;
}

export interface ConsultationVersion extends ParameterGovernance {
  id: string;
  kind: ConsultationKind;
  /** "Ativar regras", guardado e sem efeito ate a homologacao (PAR-144). */
  values: { rules_enabled: boolean };
}

/** Dados de acesso sem o segredo: o segredo nunca volta pela API. */
export interface ConsultationCredential {
  provider_key: string | null;
  username: string;
  secret_configured: boolean;
  secret_set_at: string | null;
  updated_label: string;
  updated_at: string;
}

export interface ConsultationSetting {
  kind: ConsultationKind;
  label: string;
  active_setting: ConsultationVersion | null;
  draft_setting: ConsultationVersion | null;
  credential: ConsultationCredential | null;
  /** Falso quando a chave de segredos da aplicacao nao esta configurada. */
  vault_available: boolean;
}

export interface ConsultationCredentialInput {
  provider_key?: string;
  username?: string;
  /** Ausente mantem o segredo gravado. */
  secret?: string;
}

export interface ConsultationHistoryEntry {
  id: string;
  action: string;
  actor_label: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  occurred_at: string;
}

export const consultationSettings = {
  list: (signal?: AbortSignal) =>
    api.get<ConsultationSetting[]>('/api/v1/settings/consultation', signal),
  saveDraft: (
    kind: ConsultationKind,
    input: { rules_enabled?: boolean; notes?: string },
  ) => api.put<ConsultationVersion>(`/api/v1/settings/consultation/${kind}`, input),
  activate: (kind: ConsultationKind, justification: string) =>
    api.post<ConsultationVersion>(`/api/v1/settings/consultation/${kind}/activation`, {
      justification,
    }),
  setCredential: (kind: ConsultationKind, input: ConsultationCredentialInput) =>
    api.put<ConsultationCredential>(
      `/api/v1/settings/consultation/${kind}/credential`,
      input,
    ),
  removeCredential: (kind: ConsultationKind) =>
    api.delete<void>(`/api/v1/settings/consultation/${kind}/credential`),
  history: (signal?: AbortSignal) =>
    api.get<ConsultationHistoryEntry[]>(
      '/api/v1/settings/consultation/history',
      signal,
    ),
};

// --- Endosso de aniversario e renovacao (P-PAR-3) -----------------------------------
//
// Parametros guardados com versao. Nenhuma rotina de aniversario, renovacao automatica
// ou cancelamento automatico roda, e nenhum aviso e enviado: as respostas trazem
// `automation_enabled` e `notice_delivery_enabled` sempre falsos.

/** Indice de reajuste do catalogo da plataforma: so codigo e nome. */
export interface EconomicIndex {
  code: string;
  name: string;
  description: string;
  sort_order: number;
  active: boolean;
}

/** A modalidade do cadastro, com o produto (ramo). */
export interface RenewalModalityRef {
  id: string;
  name: string;
  product_name: string;
  active: boolean;
}

export interface AnniversaryValues {
  advance_notice_days: number | null;
  boleto_due_days: number | null;
  /** `taxa_da_apolice`, a unica opcao documentada (PRC-009 PENDENTE). */
  rate_source: string | null;
  notice_email_subject: string;
  notice_email_message: string;
  notice_email_message_characters: number;
  object_description: string;
  object_description_characters: number;
  notice_email_subject_max_characters: number;
  notice_email_message_max_characters: number;
  object_description_max_characters: number;
}

export interface AnniversaryVersion extends ParameterGovernance {
  id: string;
  kind: 'aniversario';
  values: AnniversaryValues;
}

export interface AnniversarySettings {
  kind: 'aniversario';
  label: string;
  active_setting: AnniversaryVersion | null;
  draft_setting: AnniversaryVersion | null;
  automation_enabled: boolean;
  notice_delivery_enabled: boolean;
  pending_rules: string[];
  rate_sources: string[];
}

/** O "Salvar" da configuracao: ausente fica como esta; nulo limpa. */
export interface AnniversaryDraftInput {
  notes?: string;
  advance_notice_days?: number | null;
  boleto_due_days?: number | null;
  rate_source?: string | null;
  notice_email_subject?: string;
  notice_email_message?: string;
  object_description?: string;
}

export interface AnniversaryModalityVersion extends ParameterGovernance {
  id: string;
  modality_id: string;
  values: { economic_index: string | null };
}

export interface AnniversaryModality {
  /** A versao que a tela edita: o rascunho, ou a ativa sem rascunho. */
  setting_id: string;
  modality: RenewalModalityRef;
  active_setting: AnniversaryModalityVersion | null;
  draft_setting: AnniversaryModalityVersion | null;
  versions?: AnniversaryModalityVersion[];
}

export interface RenewalValues {
  boleto_due_days: number | null;
  notice_email_subject: string;
  notice_email_message: string;
  notice_email_message_characters: number;
  notice_email_subject_max_characters: number;
  notice_email_message_max_characters: number;
  notified_roles: { role_id: string; name: string }[];
}

export interface RenewalVersion extends ParameterGovernance {
  id: string;
  kind: 'renovacao_automatica';
  values: RenewalValues;
}

export interface RenewalSettings {
  kind: 'renovacao_automatica';
  label: string;
  active_setting: RenewalVersion | null;
  draft_setting: RenewalVersion | null;
  automation_enabled: boolean;
  notice_delivery_enabled: boolean;
  pending_rules: string[];
}

export interface RenewalDraftInput {
  notes?: string;
  boleto_due_days?: number | null;
  notice_email_subject?: string;
  notice_email_message?: string;
}

/** Um perfil da corretora da lista "Perfis de Usuario para Email". */
export interface RenewalNotifiedRole {
  role_id: string;
  name: string;
  broker_scoped: boolean;
  /** Recebe o aviso na versao que a tela edita. */
  notified: boolean;
  in_active: boolean;
  in_draft: boolean;
}

export interface RenewalModalityValues {
  max_term_days: number | null;
  quote_lead_days: number | null;
  auto_cancel_after_days: number | null;
  issuance_deadline_days: number | null;
  /** Sempre falso: ligar e recusado ate a homologacao juridica (PJ-01). */
  auto_cancel_enabled: boolean;
  auto_issue_enabled: boolean;
  bypass_available_limit: boolean;
  economic_index: string | null;
}

export interface RenewalModalityVersion extends ParameterGovernance {
  id: string;
  modality_id: string;
  values: RenewalModalityValues;
}

export interface RenewalModality {
  setting_id: string;
  modality: RenewalModalityRef;
  active_setting: RenewalModalityVersion | null;
  draft_setting: RenewalModalityVersion | null;
  versions?: RenewalModalityVersion[];
  automation_enabled: boolean;
}

export type RenewalModalityInput = Partial<RenewalModalityValues> & { notes?: string };

/** Filtro do historico: a configuracao ou as modalidades. */
export type RenewalHistoryScope = 'configuracao' | 'modalidades';

function renewalScopeQuery(scope?: RenewalHistoryScope): string {
  return scope ? `?scope=${scope}` : '';
}

export const economicIndices = {
  list: (signal?: AbortSignal) =>
    api.get<EconomicIndex[]>('/api/v1/catalogs/economic-indices', signal),
};

export const anniversarySettings = {
  get: (signal?: AbortSignal) =>
    api.get<AnniversarySettings>('/api/v1/settings/anniversary-endorsement', signal),
  saveDraft: (input: AnniversaryDraftInput) =>
    api.put<AnniversaryVersion>('/api/v1/settings/anniversary-endorsement', input),
  versions: (signal?: AbortSignal) =>
    api.get<AnniversaryVersion[]>(
      '/api/v1/settings/anniversary-endorsement/versions',
      signal,
    ),
  activate: (justification: string) =>
    api.post<AnniversaryVersion>(
      '/api/v1/settings/anniversary-endorsement/activation',
      {
        justification,
      },
    ),
  history: (scope?: RenewalHistoryScope, signal?: AbortSignal) =>
    api.get<ConsultationHistoryEntry[]>(
      `/api/v1/settings/anniversary-endorsement/history${renewalScopeQuery(scope)}`,
      signal,
    ),
  listModalities: (signal?: AbortSignal) =>
    api.get<AnniversaryModality[]>(
      '/api/v1/settings/anniversary-endorsement/modalities',
      signal,
    ),
  getModality: (settingId: string, signal?: AbortSignal) =>
    api.get<AnniversaryModality>(
      `/api/v1/settings/anniversary-endorsement/modalities/${settingId}`,
      signal,
    ),
  createModality: (input: {
    modality_id: string;
    economic_index?: string | null;
    notes?: string;
  }) =>
    api.post<AnniversaryModalityVersion>(
      '/api/v1/settings/anniversary-endorsement/modalities',
      input,
    ),
  saveModality: (
    settingId: string,
    input: { economic_index?: string | null; notes?: string },
  ) =>
    api.patch<AnniversaryModalityVersion>(
      `/api/v1/settings/anniversary-endorsement/modalities/${settingId}`,
      input,
    ),
  activateModality: (settingId: string, justification: string) =>
    api.post<AnniversaryModalityVersion>(
      `/api/v1/settings/anniversary-endorsement/modalities/${settingId}/activation`,
      { justification },
    ),
};

export const renewalSettings = {
  get: (signal?: AbortSignal) =>
    api.get<RenewalSettings>('/api/v1/settings/renewal', signal),
  saveDraft: (input: RenewalDraftInput) =>
    api.put<RenewalVersion>('/api/v1/settings/renewal', input),
  versions: (signal?: AbortSignal) =>
    api.get<RenewalVersion[]>('/api/v1/settings/renewal/versions', signal),
  activate: (justification: string) =>
    api.post<RenewalVersion>('/api/v1/settings/renewal/activation', { justification }),
  history: (scope?: RenewalHistoryScope, signal?: AbortSignal) =>
    api.get<ConsultationHistoryEntry[]>(
      `/api/v1/settings/renewal/history${renewalScopeQuery(scope)}`,
      signal,
    ),
  notifiedRoles: (signal?: AbortSignal) =>
    api.get<RenewalNotifiedRole[]>('/api/v1/settings/renewal/notified-roles', signal),
  addNotifiedRole: (roleId: string) =>
    api.post<RenewalVersion>('/api/v1/settings/renewal/notified-roles', {
      role_id: roleId,
    }),
  removeNotifiedRole: (roleId: string) =>
    api.delete<RenewalVersion>(`/api/v1/settings/renewal/notified-roles/${roleId}`),
  listModalities: (signal?: AbortSignal) =>
    api.get<RenewalModality[]>('/api/v1/settings/renewal/modalities', signal),
  getModality: (settingId: string, signal?: AbortSignal) =>
    api.get<RenewalModality>(
      `/api/v1/settings/renewal/modalities/${settingId}`,
      signal,
    ),
  createModality: (input: RenewalModalityInput & { modality_id: string }) =>
    api.post<RenewalModalityVersion>('/api/v1/settings/renewal/modalities', input),
  saveModality: (settingId: string, input: RenewalModalityInput) =>
    api.patch<RenewalModalityVersion>(
      `/api/v1/settings/renewal/modalities/${settingId}`,
      input,
    ),
  activateModality: (settingId: string, justification: string) =>
    api.post<RenewalModalityVersion>(
      `/api/v1/settings/renewal/modalities/${settingId}/activation`,
      { justification },
    ),
};
